import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus } from "lucide-react";
import { Bouton } from "@/composants/ui/bouton";
import { Carte, CorpsCarte, EnTeteCarte } from "@/composants/ui/carte";
import { ChampSelection } from "@/composants/ui/champ";
import { Erreur } from "@/composants/ui/etats";
import { Etiquette } from "@/composants/ui/etiquette";
import { messageDe, useChargement } from "@/lib/chargement";
import { horodatage } from "@/lib/libelles";
import { api, type ModeleDisponible, type StatutFiche } from "@/lib/api";

/** L'état d'une fiche, en toutes lettres (ADR-0024). */
export const STATUTS_FICHE: Record<StatutFiche, string> = {
  BROUILLON: "Brouillon",
  VALIDEE: "Validée",
  SIGNEE: "Signée",
  ABANDONNEE: "Abandonnée",
};

export function tonStatutFiche(statut: StatutFiche) {
  if (statut === "VALIDEE" || statut === "SIGNEE") return "succes" as const;
  return statut === "BROUILLON" ? ("actif" as const) : ("neutre" as const);
}

/**
 * Les fiches d'un lot (ADR-0060, ticket #107) : modèle, version, statut et date, la
 * plus récente d'abord. Le serveur ne rend que le périmètre de l'appelant. Qui peut
 * déposer sur le lot ouvre une fiche nouvelle d'après la version en usage d'un modèle.
 */
export function FichesDuLot({ lotId, ecriture }: { lotId: string; ecriture: boolean }) {
  const naviguer = useNavigate();
  const liste = useChargement((signal) => api.fichesDuLot(lotId, signal), [lotId]);
  const [ouverture, setOuverture] = useState<ModeleDisponible[] | null>(null);
  const [choisi, setChoisi] = useState("");
  const [erreur, setErreur] = useState<string | null>(null);

  async function ouvrir() {
    setErreur(null);
    try {
      const modeles = await api.modelesDisponibles();
      setOuverture(modeles);
      setChoisi(modeles[0]?.id ?? "");
    } catch (cause) {
      setErreur(messageDe(cause));
    }
  }

  async function creer() {
    setErreur(null);
    try {
      const fiche = await api.creerFiche(lotId, choisi);
      naviguer(`/fiches/${fiche.id}`);
    } catch (cause) {
      setErreur(messageDe(cause));
    }
  }

  const fiches = liste.donnees ?? [];
  return (
    <Carte>
      <EnTeteCarte className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-sm font-semibold text-encre">Fiches</h2>
        {ecriture && ouverture === null ? (
          <Bouton taille="compact" onClick={() => void ouvrir()}>
            <Plus className="h-3.5 w-3.5" aria-hidden />
            Nouvelle fiche
          </Bouton>
        ) : null}
      </EnTeteCarte>
      <CorpsCarte className="flex flex-col gap-3">
        {erreur ? <Erreur message={erreur} /> : null}
        {liste.erreur ? <Erreur message={liste.erreur} onReessayer={liste.recharger} /> : null}
        {ouverture !== null ? (
          ouverture.length === 0 ? (
            <p className="text-sm text-encre-douce">
              Aucun modèle n'a de version en usage : l'Administrateur doit en publier une.
            </p>
          ) : (
            <form
              className="flex flex-wrap items-end gap-2"
              onSubmit={(evenement) => {
                evenement.preventDefault();
                void creer();
              }}
            >
              <div className="min-w-0 flex-1">
                <ChampSelection
                  libelle="Modèle de la fiche"
                  value={choisi}
                  onChange={(e) => setChoisi(e.target.value)}
                  options={ouverture.map((m) => ({
                    valeur: m.id,
                    libelle: `${m.nom} — version ${m.version_numero}`,
                  }))}
                />
              </div>
              <Bouton type="submit" variante="principal">
                Créer la fiche
              </Bouton>
              <Bouton type="button" variante="discret" onClick={() => setOuverture(null)}>
                Annuler
              </Bouton>
            </form>
          )
        ) : null}
        {liste.donnees && fiches.length === 0 ? (
          <p className="text-sm text-encre-douce">Aucune fiche sur ce lot.</p>
        ) : null}
        {fiches.length > 0 ? (
          <ul className="divide-y divide-filet">
            {fiches.map((fiche) => (
              <li
                key={fiche.id}
                className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1 py-2"
              >
                <Link
                  to={`/fiches/${fiche.id}`}
                  className="min-w-0 break-words text-sm font-medium text-encre underline-offset-2 hover:underline"
                >
                  {fiche.modele_nom}, version {fiche.version_numero}
                </Link>
                <span className="flex flex-wrap items-center gap-2 text-xs text-encre-douce">
                  <Etiquette ton={tonStatutFiche(fiche.statut)}>
                    {STATUTS_FICHE[fiche.statut]}
                  </Etiquette>
                  {fiche.figee_le
                    ? `figée le ${horodatage(fiche.figee_le)}`
                    : `créée le ${horodatage(fiche.cree_le)}`}
                </span>
              </li>
            ))}
          </ul>
        ) : null}
      </CorpsCarte>
    </Carte>
  );
}
